import { useEffect, useRef, useState } from 'react';
import { UseVoiceRecordingOptions, UseVoiceRecordingReturn } from './types';

const useVoiceRecording = ({
  onTranscription,
  silenceThreshold = 30,
  silenceDuration = 1500,
  minRecordingTime = 500
}: UseVoiceRecordingOptions): UseVoiceRecordingReturn => {
  // State management
  const [isRecording, setIsRecording] = useState<boolean>(false);
  const [isTranscribing, setIsTranscribing] = useState<boolean>(false);
  const [isListening, setIsListening] = useState<boolean>(false);
  const [audioPermission, setAudioPermission] = useState<boolean | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Refs for audio management
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const silenceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const vadIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const streamRef = useRef<MediaStream | null>(null);

  // Do not request microphone permission on mount; defer to user action
  useEffect(() => {
    // Feature detection only; permission will be requested on click
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (!('mediaDevices' in navigator) || !('getUserMedia' in navigator.mediaDevices)) {
      setAudioPermission(false);
    } else {
      // Unknown until user interacts
      setAudioPermission(null);
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanup();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cleanup = (): void => {
    // Stop recording if active
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }

    // Clean up stream
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
    }

    // Clear intervals and timeouts
    if (vadIntervalRef.current) {
      clearInterval(vadIntervalRef.current);
      vadIntervalRef.current = null;
    }

    if (silenceTimeoutRef.current) {
      clearTimeout(silenceTimeoutRef.current);
      silenceTimeoutRef.current = null;
    }

    // Close audio context
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      audioContextRef.current.close();
    }

    // Reset state
    setIsRecording(false);
    setIsListening(false);
    setIsTranscribing(false);
  };

  const startRecording = async (): Promise<void> => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      setAudioPermission(true);

      const recorder = new MediaRecorder(stream, {
        mimeType: "audio/webm;codecs=opus",
      });

      // Create AudioContext for voice activity detection
      const audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
      const source = audioCtx.createMediaStreamSource(stream);
      const analyzer = audioCtx.createAnalyser();

      analyzer.fftSize = 256;
      analyzer.smoothingTimeConstant = 0.8;
      source.connect(analyzer);

      audioContextRef.current = audioCtx;

      const audioChunks: Blob[] = [];
      let silenceStartTime: number | null = null;
      let lastSoundTime = Date.now();

      recorder.ondataavailable = (event: BlobEvent) => {
        if (event.data.size > 0) {
          audioChunks.push(event.data);
        }
      };

      recorder.onstop = async () => {
        const audioBlob = new Blob(audioChunks, { type: "audio/webm" });

        // Clean up resources
        cleanup();

        // Process the audio
        if (onTranscription) {
          setIsTranscribing(true);
          try {
            await onTranscription(audioBlob);
          } catch (error) {
            setError('Transcription failed');
          } finally {
            setIsTranscribing(false);
          }
        }
      };

      // Voice Activity Detection function
      const detectVoiceActivity = (): void => {
        if (!analyzer || recorder.state !== 'recording') {
          if (vadIntervalRef.current) {
            clearInterval(vadIntervalRef.current);
            vadIntervalRef.current = null;
          }
          return;
        }

        const bufferLength = analyzer.frequencyBinCount;
        const dataArray = new Uint8Array(bufferLength);
        analyzer.getByteFrequencyData(dataArray);

        // Calculate average volume
        const average = dataArray.reduce((sum, value) => sum + value, 0) / bufferLength;
        const recordingDuration = Date.now() - lastSoundTime;

        if (average > silenceThreshold) {
          // Sound detected
          lastSoundTime = Date.now();
          silenceStartTime = null;
          setIsListening(true);

          // Clear any existing silence timeout
          if (silenceTimeoutRef.current) {
            clearTimeout(silenceTimeoutRef.current);
            silenceTimeoutRef.current = null;
          }
        } else if (recordingDuration > minRecordingTime) {
          // Silence detected and minimum recording time passed
          if (!silenceStartTime) {
            silenceStartTime = Date.now();
            setIsListening(false);
          }

          const silenceDurationMs = Date.now() - silenceStartTime;

          if (silenceDurationMs > silenceDuration && !silenceTimeoutRef.current) {
            // Auto-stop recording after silence duration
            silenceTimeoutRef.current = setTimeout(() => {
              if (recorder && recorder.state === 'recording') {
                recorder.stop();
              }
            }, 100);
          }
        }
      };

      // Use setInterval for reliable detection
      vadIntervalRef.current = setInterval(detectVoiceActivity, 100);

      recorder.start();
      mediaRecorderRef.current = recorder;
      setIsRecording(true);
      setIsListening(true);
      setError(null);

    } catch (err) {
      setAudioPermission(false);
      setError("Microphone access failed. Please check permissions.");
    }
  };

  const stopRecording = (): void => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
    }
  };

  const toggleRecording = (): void => {
    if (isRecording) {
      stopRecording();
      return;
    }
    // Request permission and start on user interaction
    startRecording();
  };

  return {
    // State
    isRecording,
    isTranscribing,
    isListening,
    audioPermission,
    error,

    // Methods
    startRecording,
    stopRecording,
    toggleRecording,
    setError,

    // Cleanup (for manual use if needed)
    cleanup
  };
};

export default useVoiceRecording;
